<template>
  <div :data-qa="formTypeQaLocator">
    <component
      :is="Layout"
      data-qa="appointment_doctor"
    >
      <template #club-screen>
        <ClubAppointmentView
          v-if="clubDataStore.isInitClubViewVisible"
          :pdf-data="urls.clubPdfs"
          :is-direct="appointmentRegistrationTypes.isDirect"
          :coupon-request-data="clubDataStore.clubStatusData.couponData"
          :medtochka-detail-url="clubDataStore.clubStatusData.medtochkaDetailUrl"
          :data-error-from-page="clubDataStore.clubStatusData.dataErrorFromPage"
          :coupon-id="clubDataStore.clubStatusData.couponId"
          :payment-status="clubDataStore.clubStatusData.couponData.paymentStatus"
          :club-lpu-price="clubDataStore.clubStatusData.clubLpuPrice"
          :is-club-registration="clubDataStore.clubStatusData.isClubRegistration"
          :town-vrach-url="initAdditionalData.townVrachUrl"
          :server-timedelta="initAdditionalData.serverTimedelta"
          :doctor-page-url="doctorData.pageUrl"
          :slot-date-string="clubDataStore.clubStatusData.slotDateString"
          :lpu-timedelta="clubDataStore.clubStatusData.lpuTimedelta"
          :town-url="urls.town"
          is-this-parent
          @club:payment-cancelled="showDefaultSuccessPage"
        />
        <ClubAppointmentView
          v-else-if="clubDataStore.isClubModalVisible"
          :pdf-data="urls.clubPdfs"
          :is-direct="appointmentRegistrationTypes.isDirect"
          :loading-state="globalDataStore.sendingUserData"
          :coupon-request-data="clubDataStore.couponData"
          :medtochka-detail-url="globalDataStore.successData.medtochkaUrl"
          :town-vrach-url="initAdditionalData.townVrachUrl"
          :server-timedelta="initAdditionalData.serverTimedelta"
          :slot-date-string="clubDataStore.clubStatusData.slotDateString"
          :lpu-timedelta="clubDataStore.clubStatusData.lpuTimedelta"
          :doctor-page-url="doctorData.pageUrl"
          :town-url="urls.town"
          @club:payment-cancelled="showSuccessPage"
        />
      </template>

      <template #state-screen-flat>
        <SuccessScreenMedtochka
          v-if="globalDataStore.successData.medtochkaUrl"
          :medtochka-url="globalDataStore.successData.medtochkaUrl"
          :town-url="urls.town"
        />
      </template>

      <template #state-screen>
        <AppointmentPreloader v-if="globalDataStore.sendingUserData" />
        <AppointmentErrorPage
          v-else-if="globalDataStore.errorData.isEnabled && !globalDataStore.errorData.isDialogRender"
          :daily-limit="initAppointmentStatusData.dailyLimit"
        />
        <AppointmentSuccess v-else-if="globalDataStore.successData.isEnabled" />
        <AppointmentDontWork v-else-if="appointmentDataStore.isDontWorkLpuWithExpress" />
      </template>

      <template #title>
        <MainTitle
          :class="{
            'mt-2': isMobile,
            'mb-6': !isMobile,
          }"
        />
      </template>

      <template #main-content>
        <DoctorCard
          v-if="doctorData.fullName"
          :doctor="doctorData"
          :is-any-telemed="appointmentDataStore.isAnyTelemed"
        />

        <template v-if="!appointmentRegistrationTypes.isDirect && appointmentDataStore.isShowPhoneOnly">
          <LpuCard
            v-if="lpuData.name && !appointmentDataStore.isAnyTelemed"
            :lpu="lpuData"
          />
          <AppointmentExpressOnlyPhone />
          <PersonalData />
        </template>
        <template v-else>
          <div
            class="d-flex flex-column"
            :class="isMobile ? 'gr-1' : 'gr-4'"
          >
            <LpuCard
              v-if="lpuData.name && !appointmentDataStore.isAnyTelemed"
              :lpu="lpuData"
            />
            <AppointmentEditableCalendar
              v-if="appointmentRegistrationTypes.isDirect"
            />
            <template v-else-if="appointmentRegistrationTypes.isExpress">
              <AppointmentEditableCalendar
                v-if="!appointmentDataStore.appointmentPlaceTypes.isHome"
              />
              <AppointmentCallTime
                v-show="!!appointmentDataStore.availableAppointmentCallTimes.length"
              />
            </template>

            <div class="mb-n4">
              <AppointmentPatientCard />
            </div>
          </div>

          <DoctorSpecialities
            v-if="!appointmentDataStore.isAnyTelemed"
          />
          <AppointmentRestrictions />
          <AppointmentFormConfirmation />
        </template>
      </template>
      <template
        v-if="!isMobile"
        #aside-content
      >
        <AppointmentSidebar />
      </template>

      <AppointmentErrorDialog
        v-if="globalDataStore.errorData.isEnabled && globalDataStore.errorData.isDialogRender"
        :daily-limit="initAppointmentStatusData.dailyLimit"
      />
    </component>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted } from 'vue'

import { deleteQueryParam } from 'www/utils'
import {
  useClubDataStore,
  useGlobalDataStore,
  useDoctorDataStore,
  useYandexGoalsStore,
  useCalendarDataStore,
  useAppointmentDataStore,
} from 'www/vue_components/common/AppointmentPage/stores'

import { getIdFromQuery } from 'www/vue_components/common/AppointmentPage/functions'
import { RECURSIVE_INTERVAL_SCHEDULE_CHECKS_DOCTOR_MS } from 'www/vue_components/common/AppointmentPage/constants'

import ClubAppointmentView from 'www/vue_components/common/Club/components/view/ClubAppointmentView/ClubAppointmentView.vue'
import AppointmentCallTime from 'www/vue_components/common/AppointmentPage/components/common/AppointmentCallTime.vue'
import AppointmentEditableCalendar from 'www/vue_components/common/AppointmentPage/components/common/AppointmentEditableCalendar.vue'
import AppointmentErrorDialog from 'www/vue_components/common/AppointmentPage/components/common/AppointmentErrorDialog.vue'
import AppointmentErrorPage from 'www/vue_components/common/AppointmentPage/components/common/AppointmentErrorPage.vue'
import AppointmentFormConfirmation from 'www/vue_components/common/AppointmentPage/components/common/AppointmentFormConfirmation.vue'
import AppointmentPatientCard from 'www/vue_components/common/FamilyProfiles/components/AppointmentPatientCard.vue'
import AppointmentPreloader from 'www/vue_components/common/AppointmentPage/components/common/AppointmentPreloader.vue'
import AppointmentSuccess from 'www/vue_components/common/AppointmentPage/components/common/AppointmentSuccess.vue'
import SuccessScreenMedtochka from 'www/vue_components/common/AppointmentPage/components/common/SuccessScreenMedtochka.vue'
import AppointmentDontWork from 'www/vue_components/common/AppointmentPage/components/common/AppointmentDontWork.vue'

import type { DoctorAppointmentFormProps } from 'src/components/pages/appointment/vrach/new/components/DoctorAppointmentForm/types'
import useMergePropsWithStores from 'src/components/pages/appointment/vrach/new/components/DoctorAppointmentForm/composables/useMergePropsWithStores'
import AppointmentExpressOnlyPhone from 'src/components/pages/appointment/vrach/new/components/DoctorAppointmentForm/components/AppointmentExpressOnlyPhone.vue'
import AppointmentRestrictions from 'src/components/pages/appointment/vrach/new/components/DoctorAppointmentForm/components/AppointmentRestrictions.vue'
import AppointmentSidebar from 'src/components/pages/appointment/vrach/new/components/DoctorAppointmentForm/components/AppointmentSidebar.vue'
import DoctorCard from 'src/components/pages/appointment/vrach/new/components/DoctorAppointmentForm/components/DoctorCard/DoctorCard.vue'
import DoctorSpecialities from 'src/components/pages/appointment/vrach/new/components/DoctorAppointmentForm/components/DoctorSpecialities.vue'
import MainTitle from 'src/components/pages/appointment/vrach/new/components/DoctorAppointmentForm/components/MainTitle.vue'
import LayoutMobile from 'src/components/pages/appointment/vrach/new/components/DoctorAppointmentForm/layouts/LayoutMobile.vue'
import LayoutDesktop from 'src/components/pages/appointment/vrach/new/components/DoctorAppointmentForm/layouts/LayoutDesktop.vue'
import LpuCard from 'src/components/pages/appointment/vrach/new/components/DoctorAppointmentForm/components/LpuCard.vue'
import PersonalData from 'src/components/pages/appointment/vrach/new/components/DoctorAppointmentForm/components/PersonalData.vue'

const props: DoctorAppointmentFormProps = defineProps<{
  urls: {
    iconCancel: string
    iconSuccess: string
    town: string
    townLpu: string | ''
    townDoctor: string | ''
    referrerToDoctorList: string | ''
    clubPdfs: {
      coupon: string
      participation: string
    }
  }

  initUserData: {
    name: string | ''
    surname: string | ''
    patronymic: string | ''
    birthDay: string | ''
    phone: string | ''
  }

  initCalendarData: {
    days: 30
    day: string
    time: string
    dateNow: string
    dateName: string
    inputDay: string
    isSlotFixed: boolean
    customRequestLimit: number
    doctorsAndLpus: Array<{
      doctorId: number
      lpuId: number
    }>
    lpuTimedeltas: Array<[number, number]>
  }

  initAdditionalData: {
    specialityId: number | null
    placeType: string | ''
    serverTimedelta: number
    townVrachUrl: string
  }

  appointmentRegistrationTypes: {
    isDirect: boolean
    isExpress: boolean
  }

  appointmentObjectTypes: {
    isDoctor: true
  }

  appointmentCallTimes?: {
    minHoursToCall: 2
    date: string
    times: Array<{
      text: string
      value: string
      date: string
    }>
  }

  lpuData: {
    id: number | null
    name: string
    address: string
    phone: string
    schedule: Record<string, {
      timeStart: string
      timeEnd: string
    }>
    townName: string
    townTimeDelta: number
    nearestAppointmentExpress: string | ''
    telemedContractType?: 'telemed_medical' | 'telemed_info'
  }

  doctorData: {
    id: number | null
    fullName: string
    imageSrc: string
    experience: string | ''
    category: string | ''
    science: string | ''
    pageUrl: string
    workplaces: Array<{
      id: number
      isInClub: boolean
      clubDiscount: number
      speciality: {
          id: number
          name: string
      }
      priceList: {
          price: number | null
          priceHome: number | null
          priceOnline: number | null
          priceMedtochkaTelemed: number | null
          priceClub: number | null
          priceClubLpu: number | null
          priceClubCoupon: number | null
      }
      age: {
          min: number | null
          max: number | null
      }
      isShowPhoneOnly: boolean
      appointmentRestrictions: string[]
    }>
  }

  initAppointmentStatusData: {
    isError: boolean
    isSuccess: boolean
    code: string | ''
    title: string | ''
    subtitle: string | ''
    medtochkaUrl: string | ''
    buttonState: {
      get: string | ''
      post: string | ''
    }
    isEnableMedtochkaRedirect: false
    dailyLimit: number
  }

  initClubStatusData?: {
    couponData: {
      id: string
      dateCreated: string
      lifeTime: number
      lifeTimeSeconds: number | null
      clubCouponPrice: number
      fullPrice: number
      price: number
      savingPrice: number
      clubLpuPrice: number
      paymentStatus: 'canceled' | 'succeeded' | 'created'
      cardPrice: number
      totalPrice: string
      clubCardLifetimeMonths: string
      email: string | ''
    }
    dataErrorFromPage: {
      has: boolean | null
      key: string | ''
    }
    slotDateString: string | ''
    lpuTimedelta: number | null
    medtochkaDetailUrl: string | ''
    couponId: string
    clubLpuPrice: number
    isClubRegistration: boolean
  }
}>()

useMergePropsWithStores(props)

const isMobile = window.MOBILE_VERSION

const appointmentDataStore = useAppointmentDataStore()
const doctorDataStore = useDoctorDataStore()
const globalDataStore = useGlobalDataStore()
const clubDataStore = useClubDataStore()

const Layout = computed(() => (isMobile ? LayoutMobile : LayoutDesktop))
const formTypeQaLocator = computed(() => (
  props.appointmentRegistrationTypes.isExpress
    ? 'appointment_express'
    : 'appointment_direct'
))

const hasTargetDoctorId = getIdFromQuery('targetDoctorId') !== undefined
const hasDoctorId = getIdFromQuery('doctorId') !== undefined

if (hasDoctorId && hasTargetDoctorId) {
  deleteQueryParam('doctorId')
}

const yandexGoalsStore = useYandexGoalsStore()
const calendarDataStore = useCalendarDataStore()

onMounted(() => {
  if (
    !globalDataStore.errorData.isEnabled
    && !globalDataStore.successData.isEnabled
    && props.appointmentObjectTypes.isDoctor
  ) {
    setTimeout(() => {
      calendarDataStore.startRecursiveUpdateDoctorSchedule()
    }, RECURSIVE_INTERVAL_SCHEDULE_CHECKS_DOCTOR_MS)
  }

  if (!globalDataStore.errorData.isEnabled && !globalDataStore.successData.isEnabled) {
    // Открытие формы записи на Телемед
    yandexGoalsStore.sendYaGoal({
      name: 'townOnline__viewAppointmentTelemed',
      formType: 'telemedAndTownOnline',
      objectType: 'doctor',
    })
    setTimeout(() => {
      if (appointmentDataStore.isClubPossibleOnPage && doctorDataStore.isSomeWorkplaceInClub) {
        yandexGoalsStore.sendYaGoal({
          name: 'club__appointmentForm',
          formType: 'directClub',
          objectType: 'doctor',
        })
      }
    })
    yandexGoalsStore.sendYaGoal({
      name: 'appointment__openFormDirect',
      formType: 'direct',
      objectType: 'doctor',
    })
    // Пользователь перешёл в форму записи
    yandexGoalsStore.sendYaGoal({
      name: 'telemed__appointmentOpen',
      formType: 'telemed',
      objectType: 'doctor',
    })
  }

  appointmentDataStore.updateAppointmentNode(document.querySelector('#doctor-appointment-form'))
})

function showSuccessPage() {
  globalDataStore.updateSuccessDataIsEnabled(true)
  clubDataStore.updateStateIsClubModalVisible(false)
}

function showDefaultSuccessPage() {
  globalDataStore.showDefaultSuccess()
  clubDataStore.updateStateIsInitClubViewVisibilityAllowed(false)
}
</script>
